/**
 * Modal to choose which product_suppliers back the listing and the selected quantity and priority per supplier.
 * Uses the same eligible list as listing defaults (active, shared_stock, stored_location in Home/Meli).
 */
import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';

function toInt(value, fallback = 0) {
  const n = value === '' || value == null ? fallback : parseInt(String(value), 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * @param {{
 *   show: boolean,
 *   onHide: () => void,
 *   eligibleProductSuppliers: Array<{ id?: number, origin?: string, stored_location?: string, available_amount?: number | null, default_sale_price?: number | null, unit_price_currency?: string, [key: string]: unknown }>,
 *   initialSelection: { [products_suppliers_id: number]: { selected_quantity: number, priority?: number } },
 *   onSave: (entries: Array<{ products_suppliers_id: number, selected_quantity: number, priority: number }>) => void,
 *   modalTitle?: string
 * }} props
 */
const PickListingSuppliersModal = ({
  show,
  onHide,
  eligibleProductSuppliers,
  initialSelection,
  onSave,
  modalTitle = 'Pick listing suppliers'
}) => {
  const [selected, setSelected] = useState({});
  const [quantities, setQuantities] = useState({});
  const [priorities, setPriorities] = useState({});

  useEffect(() => {
    if (!show) return;
    const sel = {};
    const qty = {};
    const pri = {};
    eligibleProductSuppliers.forEach((ps) => {
      const id = ps.id;
      if (id == null) return;
      const initial = initialSelection && initialSelection[id];
      const isObj = initial != null && typeof initial === 'object';
      const hasQty = isObj && initial.selected_quantity != null ? true : Number.isFinite(initial);
      sel[id] = true;
      qty[id] = hasQty ? (isObj ? toInt(initial.selected_quantity, 0) : toInt(initial, 0)) : toInt(ps.available_amount, 0);
      pri[id] = isObj && initial.priority != null ? toInt(initial.priority, 0) : 0;
    });
    setSelected(sel);
    setQuantities(qty);
    setPriorities(pri);
  }, [show, eligibleProductSuppliers, initialSelection]);

  const handleToggle = (psId) => {
    setSelected((prev) => ({ ...prev, [psId]: !prev[psId] }));
  };

  const handleQuantityChange = (psId, value) => {
    const n = Math.max(0, toInt(value, 0));
    setQuantities((prev) => ({ ...prev, [psId]: n }));
  };

  const handlePriorityChange = (psId, value) => {
    const n = toInt(value, 0);
    setPriorities((prev) => ({ ...prev, [psId]: n }));
  };

  const handleSave = () => {
    const entries = eligibleProductSuppliers
      .filter((ps) => ps.id != null && selected[ps.id] === true)
      .map((ps) => ({
        products_suppliers_id: ps.id,
        selected_quantity: Math.max(0, toInt(quantities[ps.id], 0)),
        priority: toInt(priorities[ps.id], 0)
      }))
      .filter((e) => e.selected_quantity > 0);
    onSave(entries);
    onHide();
  };

  const totalSelected = eligibleProductSuppliers
    .filter((ps) => ps.id != null && selected[ps.id] === true)
    .reduce((sum, ps) => sum + Math.max(0, toInt(quantities[ps.id], 0)), 0);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="modal-dark">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Choose which product-supplier links to include and set quantity and priority for each. The listing&apos;s available quantity is the sum of the selected quantities (edit only in this modal).
        </p>
        {eligibleProductSuppliers.length === 0 ? (
          <p className="mb-0">No eligible product-suppliers (active, shared stock, stored at Home or Meli).</p>
        ) : (
          <Table size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th style={{ width: '2.5rem' }}>Include</th>
                <th>Origin</th>
                <th>Stored location</th>
                <th>Available amount</th>
                <th>Default sale price</th>
                <th>Unit curr.</th>
                <th>Selected quantity</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {eligibleProductSuppliers.map((ps) => {
                const id = ps.id;
                if (id == null) return null;
                const isChecked = selected[id] === true;
                const qty = quantities[id];
                const pri = priorities[id];
                return (
                  <tr key={id}>
                    <td className="align-middle">
                      <Form.Check
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggle(id)}
                        aria-label={`Include supplier ${ps.origin || id}`}
                      />
                    </td>
                    <td className="align-middle">{ps.origin ?? '—'}</td>
                    <td className="align-middle">{ps.stored_location ?? '—'}</td>
                    <td className="align-middle">{ps.available_amount != null ? Number(ps.available_amount) : '—'}</td>
                    <td className="align-middle">{ps.default_sale_price != null ? Number(ps.default_sale_price) : '—'}</td>
                    <td className="align-middle">{ps.unit_price_currency || ps.sale_price_currency || '—'}</td>
                    <td className="align-middle">
                      <Form.Control
                        type="number"
                        min={0}
                        step={1}
                        value={isChecked ? (qty ?? '') : ''}
                        disabled={!isChecked}
                        onChange={(e) => handleQuantityChange(id, e.target.value)}
                        size="sm"
                        style={{ maxWidth: '6rem' }}
                        aria-label={`Selected quantity for ${ps.origin || id}`}
                      />
                    </td>
                    <td className="align-middle">
                      <Form.Control
                        type="number"
                        step={1}
                        value={isChecked ? (pri ?? '') : ''}
                        disabled={!isChecked}
                        onChange={(e) => handlePriorityChange(id, e.target.value)}
                        size="sm"
                        style={{ maxWidth: '5rem' }}
                        aria-label={`Priority for ${ps.origin || id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {eligibleProductSuppliers.length > 0 && (
          <p className="small mt-2 mb-0 text-muted">
            Total selected quantity: <strong>{totalSelected}</strong>
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={eligibleProductSuppliers.length === 0 || totalSelected <= 0}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PickListingSuppliersModal;
